import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Alert, Box, FormControlLabel, Grid, Stack, Switch, Tab, Tabs, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import { InformationBox } from '../../presentation-components/miscellaneous-components/information-box'
import { ClosedReportPeriodMessage } from '../../presentation-components/miscellaneous-components/closed-report-period-message'
import { StartNewReportPeriodDialog } from '../../dialogs/settings/report-period/start-new-report-period-dialog'
import { AnalyticMetric } from '../../presentation-components/analytics-components/graphs/analytic-metric'
import { ReadinessStateChip } from '../../presentation-components/readiness-components/readiness-state-chip'
import { ReadinessChecksList } from '../../presentation-components/readiness-components/readiness-checks-list'
import { ReadinessTasksPanel } from '../../presentation-components/readiness-components/readiness-tasks-panel'
import { AcknowledgeFindingDialog } from '../../presentation-components/readiness-components/acknowledge-finding-dialog'
import { CreateTaskFromFindingDialog } from '../../presentation-components/readiness-components/create-task-from-finding-dialog'
import ReadinessService from '../../../shared/services/readiness/readiness.service'
import UserDetailsService from '../../../shared/services/user-details/user-details.service'
import { IReadiness, IReadinessFinding } from '../../../shared/services/readiness/readiness.model'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { IconName } from '../../../shared/interfaces/icon-picker.model'

// Report Readiness: what stands between the organisation and a report it can sign. One surface,
// two feeds: derived checks (computed from the data, never tickable; they clear when the data
// changes or are acknowledged with a disclosed reason) and the assigned report actions. The
// headline is one of three named states, never a percentage.
const ACK_VIEWS = ['open', 'acknowledged', 'all'] as const
type AckView = typeof ACK_VIEWS[number]

export const ReadinessPage = () => {
  const readinessService = useMemo(() => new ReadinessService(), [])
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const [searchParams, setSearchParams] = useSearchParams()

  const [readiness, setReadiness] = useState<IReadiness | null>(null)
  const [hasFetched, setHasFetched] = useState(false)
  const [ackTarget, setAckTarget] = useState<IReadinessFinding | null>(null)
  const [createTaskTarget, setCreateTaskTarget] = useState<IReadinessFinding | null>(null)
  const [startPeriodDialogOpen, setStartPeriodDialogOpen] = useState(false)
  const [digestEnabled, setDigestEnabled] = useState<boolean>(!!userDetailsContext.userDetails?.readinessDigestEnabled)

  const reportPeriodId = reportPeriodContext.lastSelectedReportId
  const reportPeriodName = useMemo(
    () => reportPeriodContext.allReportPeriods.find(p => p.id === reportPeriodId)?.friendlyName,
    [reportPeriodContext.allReportPeriods, reportPeriodId],
  )
  const isAdmin = !!userDetailsContext.userDetails?.roles.includes('admin')

  // Tab state lives in ?tabId= so deep links land on the right feed; keyed on searchParams so
  // in-page navigation to the same route switches tabs without a remount.
  const tab = searchParams.get('tabId') === '1' ? 1 : 0
  const tabHandler = (_: React.SyntheticEvent, next: number) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('tabId', String(next))
      return params
    }, { replace: true })
  }

  const loadReadiness = useCallback(() => {
    if (!reportPeriodId) return
    readinessService.getForPeriod(reportPeriodId)
      .then(response => { setReadiness(response); setHasFetched(true) })
      .catch(err => { console.log(err); setHasFetched(true) })
  }, [reportPeriodId, readinessService])

  useEffect(() => {
    setHasFetched(false)
    setReadiness(null)
    loadReadiness()
  }, [loadReadiness])

  const canAcknowledge = isAdmin && !!readiness && !readiness.reportPeriodLocked

  const releaseHandler = (finding: IReadinessFinding) => {
    if (!finding.acknowledgement) return
    userNotificationsContext.openDialog({
      active: true,
      title: 'Remove acknowledgement',
      message: 'The finding will reopen and count against readiness again. The original acknowledgement stays on record.',
      buttonCopy: 'Remove',
      cancelButtonCopy: 'Cancel',
    }).then(confirmed => {
      if (!confirmed) return
      readinessService.releaseAcknowledgement(finding.acknowledgement!.id)
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Acknowledgement removed' })
          loadReadiness()
        })
        .catch(err => {
          const message = err?.response?.data?.message || 'There was an error, please try again'
          userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        })
    })
  }

  // The Open / Acknowledged / All view lives in ?view= like the tab, so a link (the assumptions
  // register's link from an acknowledged finding) can land on the acknowledged findings directly.
  // Open is the default and leaves the parameter off.
  const viewParam = searchParams.get('view')
  const ackView: AckView = ACK_VIEWS.includes(viewParam as AckView) ? viewParam as AckView : 'open'
  const ackViewHandler = (_: React.MouseEvent<HTMLElement>, next: AckView | null) => {
    if (!next) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (next === 'open') params.delete('view')
      else params.set('view', next)
      return params
    }, { replace: true })
  }


  const score = readiness?.score
  const openCount = readiness ? readiness.findings.length : 0
  const blockingCount = score ? score.integrity.blocks_reporting : 0

  return (
    <PageOuterContainer>
      <PageHeading
        title='Report Readiness'
        extraActions={readiness ? <ReadinessStateChip state={readiness.state} /> : undefined}
      />
      {!reportPeriodId && <>
        <ClosedReportPeriodMessage openDialog={() => setStartPeriodDialogOpen(true)} />
        {startPeriodDialogOpen && <StartNewReportPeriodDialog open={startPeriodDialogOpen} closeHandler={() => setStartPeriodDialogOpen(false)} />}
      </>}
      {reportPeriodId && <>
        <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
          What stands between you and a report you can sign{reportPeriodName ? `, for ${reportPeriodName}` : ''}. Checks are derived from your data and clear when it changes; they cannot be ticked off.
        </Typography>

        {!hasFetched && <Box sx={{ mt: 10 }}><LoadingSpinner /></Box>}

        {hasFetched && !readiness &&
          <Alert severity='error' sx={{ mb: 2 }}>Readiness could not be evaluated for this period. Please try again.</Alert>}

        {hasFetched && readiness && <>
          {readiness.reportPeriodLocked &&
            <Alert severity='info' sx={{ mb: 2 }}>This reporting period is locked. Findings are shown for reference; acknowledging and assignment are disabled.</Alert>}

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
              <AnalyticMetric
                fitContent
                title='Coverage'
                tooltip='Expected data comes from your own set-up: emission sources you have switched on, plus sites configured for monthly bills. Coverage counts how much of that expected data is present for this period.'
                value={score!.coverage.taggedGroupsWithData}
                units={`of ${score!.coverage.taggedGroupsTotal} sources with data`}
                footer={score!.coverage.locationMonthsExpected > 0
                  ? <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    Monthly readings: {score!.coverage.locationMonthsWithData} of {score!.coverage.locationMonthsExpected} expected so far
                  </Typography>
                  : undefined}
              />
            </Grid>
            <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
              <AnalyticMetric
                fitContent
                title='Data quality'
                tooltip='The share of your reported total calculated from actual usage rather than money spent, weighted by emissions. Higher is better: spend-based figures are estimates.'
                value={score!.quality.spendBasedSharePct !== null ? 100 - score!.quality.spendBasedSharePct : 0}
                units='% activity-based'
                unavailable={score!.quality.spendBasedSharePct === null}
                unavailableMessage='No emissions reported for this period yet.'
                footer={<Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                  of {score!.quality.totalTco2e.toLocaleString('en-GB', { maximumFractionDigits: 2 })} tCO2e reported
                </Typography>}
              />
            </Grid>
            <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
              <AnalyticMetric
                fitContent
                title='Critical findings'
                tooltip='Findings that make the report wrong or incomplete. Any critical finding keeps the period at Not ready.'
                value={blockingCount}
                units={blockingCount === 1 ? 'critical finding' : 'critical findings'}
                footer={<Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                  {blockingCount > 0 ? 'Fix these before generating the report.' : 'Nothing is blocking this report.'}
                </Typography>}
              />
            </Grid>
          </Grid>

          <Box sx={{ borderBottom: 1, borderColor: 'lightGrey.main', mt: 2 }}>
            <Tabs value={tab} onChange={tabHandler}>
              <Tab label={
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                  <Typography>Checks</Typography>
                  {openCount > 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'primary.main', color: 'common.white', width: '25px', height: '25px', borderRadius: '100%' }}>
                    {openCount}
                  </Box>}
                </Box>} sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
              <Tab label='Tasks' sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
            </Tabs>
          </Box>

          {tab === 0 && <Box sx={{ mt: 2 }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' useFlexGap flexWrap='wrap' gap={1}>
              <ToggleButtonGroup value={ackView} exclusive onChange={ackViewHandler} size='small'>
                <ToggleButton value='open' sx={{ py: 0.5, px: 2, textTransform: 'none' }}>Open</ToggleButton>
                <ToggleButton value='acknowledged' sx={{ py: 0.5, px: 2, textTransform: 'none' }}>Acknowledged{readiness.acknowledged.length > 0 ? ` (${readiness.acknowledged.length})` : ''}</ToggleButton>
                <ToggleButton value='all' sx={{ py: 0.5, px: 2, textTransform: 'none' }}>All</ToggleButton>
              </ToggleButtonGroup>
              <FormControlLabel
                control={<Switch
                  checked={digestEnabled}
                  onChange={() => {
                    new UserDetailsService().toggleReadinessDigest()
                      .then(response => setDigestEnabled(response.readinessDigestEnabled))
                      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' }))
                  }}
                />}
                label={<Typography variant='body2' sx={{ color: 'text.secondary' }}>Weekly email digest</Typography>}
              />
            </Stack>

            {(ackView === 'open' ? readiness.findings.length === 0 : (ackView === 'acknowledged' ? readiness.acknowledged.length === 0 : readiness.findings.length + readiness.acknowledged.length === 0))
              ? <InformationBox
                icon={IconName.CheckCircle}
                text={ackView === 'acknowledged' ? 'No findings have been acknowledged for this period.' : 'No open findings for this period.'}
                additionalText={ackView === 'acknowledged' ? undefined : 'Checks run continuously against your data, so new findings appear here on their own as it changes.'}
                marginTop={2}
              />
              : <ReadinessChecksList
                findings={readiness.findings}
                acknowledged={readiness.acknowledged}
                view={ackView}
                canAcknowledge={canAcknowledge}
                onAcknowledge={(finding) => setAckTarget(finding)}
                onRelease={releaseHandler}
                onCreateTask={(finding) => setCreateTaskTarget(finding)}
              />}
          </Box>}

          {tab === 1 && <Box sx={{ mt: 2 }}>
            <ReadinessTasksPanel reportPeriodId={reportPeriodId} locked={readiness.reportPeriodLocked} />
          </Box>}

          <AcknowledgeFindingDialog
            open={!!ackTarget}
            finding={ackTarget}
            reportPeriodId={reportPeriodId}
            closeHandler={() => setAckTarget(null)}
            onAcknowledged={loadReadiness}
          />
          <CreateTaskFromFindingDialog
            open={!!createTaskTarget}
            finding={createTaskTarget}
            reportPeriodId={reportPeriodId}
            closeHandler={() => setCreateTaskTarget(null)}
            onCreated={loadReadiness}
          />
        </>}
      </>}
    </PageOuterContainer>
  )
}
